import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Bucket from "@/services/bucket/bucket";
import type { GetBucket } from "@/dtos/Bucket.dto";

export interface BucketsParams {
  organizationId: string;
  page?: number;
  limit?: number;
  offset?: number;
  bucket?: string;
  followUp?: string;
  createdById?: string;
  contactId?: string;
  taskType?: string;
  fname?: string;
  lname?: string;
  email?: string;
  mobileNumber?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
  tagId?: string;
  callDispositionId?: string;
  callDispositionIds?: string[];
  ownerId?: string | null;
  timezones?: string[];
  hasValidPhone?: boolean | null;
  hasValidEmail?: boolean | null;
  contactedVia?: string[];
  customFieldValues?: Record<string, any>;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useBuckets(params: Ref<BucketsParams | null> | BucketsParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["buckets", "disabled"];
    return [
      "buckets",
      p.organizationId,
      p.page || 1,
      p.limit || 25,
      p.offset || 0,
      p.bucket || "",
      p.followUp || "",
      p.createdById || "",
      p.contactId || "",
      p.taskType || "",
      p.fname || "",
      p.lname || "",
      p.email || "",
      p.mobileNumber || "",
      p.jobTitle || "",
      p.company || "",
      p.industry || "",
      p.tagId || "",
      p.callDispositionId || "",
      (p.callDispositionIds || []).join(","),
      p.ownerId === undefined ? "" : String(p.ownerId),
      (p.timezones || []).join(","),
      p.hasValidPhone ?? "",
      p.hasValidEmail ?? "",
      (p.contactedVia || []).join(","),
      p.customFieldValues ? JSON.stringify(p.customFieldValues) : "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetBucket = {
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 25,
      };

      if (p.offset !== undefined) queryParams.offset = p.offset;
      if (p.bucket) queryParams.bucket = p.bucket;
      if (p.followUp) queryParams.followUp = p.followUp;
      if (p.createdById) queryParams.createdById = p.createdById;
      if (p.contactId) queryParams.contactId = p.contactId;
      if (p.taskType) queryParams.taskType = p.taskType;
      if (p.fname) queryParams.fname = p.fname;
      if (p.lname) queryParams.lname = p.lname;
      if (p.email) queryParams.email = p.email;
      if (p.mobileNumber) queryParams.mobileNumber = p.mobileNumber;
      if (p.jobTitle) queryParams.jobTitle = p.jobTitle;
      if (p.company) queryParams.company = p.company;
      if (p.industry) queryParams.industry = p.industry;
      if (p.tagId) queryParams.tagId = p.tagId;
      if (p.callDispositionId) queryParams.callDispositionId = p.callDispositionId;
      if (p.callDispositionIds && p.callDispositionIds.length > 0)
        queryParams.callDispositionIds = p.callDispositionIds;
      if (p.ownerId !== undefined) queryParams.ownerId = p.ownerId;
      if (p.timezones && p.timezones.length > 0) queryParams.timezones = p.timezones;
      if (p.hasValidPhone === true) queryParams.hasValidPhone = true;
      if (p.hasValidEmail === true) queryParams.hasValidEmail = true;
      if (p.contactedVia && p.contactedVia.length > 0) queryParams.contactedVia = p.contactedVia;
      if (p.customFieldValues && Object.keys(p.customFieldValues).length > 0) {
        queryParams.customFieldValues = JSON.stringify(p.customFieldValues);
      }

      const result = await Bucket.getBuckets(queryParams);

      if (result.data.status) {
        return {
          data: result.data.data,
          metaData: result.data.metaData,
        };
      }

      throw new Error(result.data.msg || "Failed to load buckets");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
